import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { GroupElement } from '@bynder-studio/render-web';
import { type VisualElement } from '@bynder-studio/render-core';
import { Tooltip, Divider, Dropdown } from '@bynder/design-system';
import { IconCopy, IconCreateGroup, IconDelete, IconMore, IconUngroup, IconStarredFolder } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useElementGeneralActions from 'packages/pages/editor/useElementGeneralActions';
import useGeneralShortcuts from 'packages/hooks/shortcuts/useGeneralShortcuts';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import { parseShortcutForTooltip, WithShortcutLabel } from 'packages/common/WithShortcutLabel';
import useComponentModal from 'packages/common/Components/ComponentModal';
import useAccessRights from 'packages/hooks/useAccessRights';
import useComponentOverview from 'packages/common/Components/ComponentOverview';
import useEditor from '~/hooks/useEditor';
import AddLayerButton from '~/common/editor/layers/addLayerButton/AddLayerButton';
import { collectTopLevelElements } from '~/common/editor/timeline/timeline-actions/components/utils';
import usePresets from '~/common/editor/presets/usePresets';
import useForceUpdate from '~/hooks/useForceUpdate';
import features from '~/configs/features';
import { AddElementWrapper, LayerButton } from '../addLayerButton/AddLayerButton.styled';

const LayerHeader = ({ selectedGroups, setSelectedGroups }) => {
    const { createPreset } = usePresets();
    const { toggleComponentOverview, renderComponentOverview } = useComponentOverview({
        onApplySuccess: () => {},
    });
    const { openComponentModal, renderComponentModal } = useComponentModal({
        onSaveSuccess() {
            // A potential to open overview after saving a component
            // Not required at the moment, but can be enabled in the future
            // toggleComponentOverview();
        },
    });
    const { creativeModel, manipulationRenderer } = useEditor();
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();

    const { isPlatformAllowed } = useAccessRights();
    const hasComponentGalleryFeature = isPlatformAllowed([features.COMPONENT_GALLERY]);

    const selectedElements = manipulationRenderer?.getSelectedElements() || [];
    const isGroup = useMemo(() => {
        const els = selectedElements.filter(
            (el) => !el.parent || selectedElements.findIndex((e) => e.id === el.parent.id) === -1,
        );

        return els.length === 1 && els.every((el) => el instanceof GroupElement);
    }, [selectedElements]);

    const { handleSelection, handleDelete, handleDuplicate, handleLock } = useElementGeneralActions(
        setSelectedGroups,
        selectedGroups,
    );

    const handleAddOrRemoveGroup = useCallback(() => {
        if (isGroup) {
            const elementToSelect = selectedGroups[selectedGroups.length - 1].children.reduce(
                (elA, elB) => ((elA?.renderOrder || 0) > (elB?.renderOrder || 0) ? elA : elB),
                null,
            );

            const editorElements = creativeModel.getAllElementsRecursively<VisualElement>('visual');
            const maskedElements = editorElements.filter((el) => el.mask && el.mask.elementId);

            selectedGroups.forEach((group) => {
                creativeModel.unwrapGroupElement(group.id);

                if (maskedElements) {
                    maskedElements.forEach((element) => {
                        if (group.id === element.mask.elementId) {
                            creativeModel.updateElement(element.id, {
                                mask: null,
                            });
                        }
                    });
                }

                if (group.mask && group.mask.elementId) {
                    const sameElementMaskedElements = maskedElements
                        .filter((el) => !el.id === group.id)
                        .filter((el) => el.mask.elementId === group.mask.elementId);

                    if (!sameElementMaskedElements.length) {
                        creativeModel.updateElement(group.mask.elementId, {
                            mask: null,
                        });
                    }
                }
            });

            if (elementToSelect) {
                manipulationRenderer.selectElement(elementToSelect.id);
            } else {
                setSelectedGroups([]);
            }
        } else if (selectedElements.length) {
            const groupElement = creativeModel.wrapElementsIntoGroup(selectedElements.map((el) => el.id));
            manipulationRenderer.selectElement(groupElement.id);
        }
    }, [isGroup, selectedElements, creativeModel, manipulationRenderer]);

    const handleCreatePreset = useCallback(() => {
        if (hasComponentGalleryFeature) {
            const copiedElements = collectTopLevelElements(manipulationRenderer.getSelectedElements());
            if (!copiedElements.length) {
                return;
            }

            openComponentModal('create', copiedElements);
        } else {
            if (isGroup) {
                createPreset(selectedGroups[0]);
            }
        }
    }, [createPreset, selectedGroups, isGroup, hasComponentGalleryFeature, openComponentModal]);

    const handleHide = useCallback(() => {
        collectTopLevelElements(manipulationRenderer?.getSelectedElements()).forEach((el) => {
            creativeModel.updateElement(el.id, { hidden: !el.hidden });
        });
        forceUpdate();
    }, [creativeModel, manipulationRenderer]);

    useGeneralShortcuts({
        handleDuplicate,
        handleSelection,
        handleGroup: handleAddOrRemoveGroup,
        handleUnGroup: handleAddOrRemoveGroup,
        handleDelete,
        handleLock,
        handleHide,
    });
    usePresetShortcuts({ createPreset: handleCreatePreset });

    const showActionButtons = !!selectedElements.length;

    return (
        <div className="layers--header position-relative">
            <StyledHeader>{translate('editor.layers.sidebar.header.layers')}</StyledHeader>
            <div className="layers--header-action-btns d-flex align-items-center">
                {showActionButtons && (
                    <Tooltip
                        content={
                            !isGroup
                                ? `${translate('editor.layers.sidebar.header.group')} (${parseShortcutForTooltip([
                                      'cmd',
                                      'G',
                                  ])})`
                                : `${translate('editor.layers.sidebar.header.ungroup')} (${parseShortcutForTooltip([
                                      'shift',
                                      'cmd',
                                      'G',
                                  ])})`
                        }
                    >
                        <AddElementWrapper>
                            <LayerButton
                                title=""
                                variant="clean"
                                isDisabled={!selectedElements.length}
                                onClick={handleAddOrRemoveGroup}
                                icon={!isGroup ? <IconCreateGroup /> : <IconUngroup />}
                            />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {showActionButtons && (
                    <Tooltip
                        content={`${translate('editor.layers.sidebar.header.duplicate')} (${parseShortcutForTooltip([
                            'cmd',
                            'D',
                        ])})`}
                    >
                        <AddElementWrapper>
                            <LayerButton title="" variant="clean" onClick={handleDuplicate} icon={<IconCopy />} />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {!hasComponentGalleryFeature && showActionButtons && !isGroup && (
                    <Tooltip
                        content={`${translate('editor.layers.sidebar.header.delete')} (${parseShortcutForTooltip([
                            'backspace',
                        ])})`}
                    >
                        <AddElementWrapper>
                            <LayerButton title="" variant="clean" onClick={handleDelete} icon={<IconDelete />} />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {!hasComponentGalleryFeature && showActionButtons && isGroup && (
                    <Dropdown
                        position="bottom-right"
                        trigger={({ isOpen, ...triggerProps }) => (
                            <Tooltip content={translate('editor.layers.sidebar.header.moreOptions')}>
                                <AddElementWrapper>
                                    <LayerButton
                                        title=""
                                        icon={<IconMore />}
                                        variant="clean"
                                        isDisabled={false}
                                        {...triggerProps}
                                    />
                                </AddElementWrapper>
                            </Tooltip>
                        )}
                    >
                        <Dropdown.Item icon={<IconStarredFolder />} onClick={handleCreatePreset}>
                            <WithShortcutLabel shortcut={['cmd', 'P']}>
                                {translate('editor.layers.sidebar.header.preset.save')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                        <Divider />
                        <Dropdown.Item icon={<IconDelete />} onClick={handleDelete}>
                            <WithShortcutLabel shortcut={['backspace']}>
                                {translate('editor.layers.sidebar.header.deleteGroup')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                    </Dropdown>
                )}
                {hasComponentGalleryFeature && showActionButtons && (
                    <Dropdown
                        position="bottom-right"
                        trigger={({ isOpen, ...triggerProps }) => (
                            <Tooltip content={translate('editor.layers.sidebar.header.moreOptions')}>
                                <AddElementWrapper>
                                    <LayerButton
                                        title=""
                                        icon={<IconMore />}
                                        variant="clean"
                                        isDisabled={false}
                                        {...triggerProps}
                                    />
                                </AddElementWrapper>
                            </Tooltip>
                        )}
                    >
                        <Dropdown.Item icon={<IconStarredFolder />} onClick={handleCreatePreset}>
                            <WithShortcutLabel shortcut={['cmd', 'P']}>
                                {translate('editor.layers.sidebar.header.components.save')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                        <Divider />
                        <Dropdown.Item icon={<IconDelete />} onClick={handleDelete}>
                            <WithShortcutLabel shortcut={['backspace']}>
                                {translate('editor.layers.sidebar.header.delete')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                    </Dropdown>
                )}
                <AddLayerButton />
            </div>
            {renderComponentOverview()}
            {renderComponentModal()}
        </div>
    );
};

export default LayerHeader;

const StyledHeader = styled.span`
    flex-grow: 1;
`;
